import React, { useState, useEffect, Children } from 'react';
import { jsx } from 'react/jsx-runtime';

function setRef(ref, value) {
    if (typeof ref === 'function') {
        ref(value);
    }
    else if (ref) {
        ref.current = value;
    }
}

function useForkRef(refA, refB) {
    return React.useMemo(() => {
        if (refA == null && refB == null) {
            return null;
        }
        return (refValue) => {
            setRef(refA, refValue);
            setRef(refB, refValue);
        };
    }, [refA, refB]);
}

/**
 * @deprecated
 * @param elementRef
 * @param isElementVisible
 */
const useClickedOutside = (elementRef, isElementVisible = false) => {
    const [isClickedOutside, setIsClickedOutside] = useState(false);
    useEffect(() => {
        if (!isElementVisible) {
            setIsClickedOutside(false);
            return;
        }
        const setElementContainsTarget = (event) => {
            setIsClickedOutside(!!(elementRef.current && elementRef.current.contains(event.target)));
        };
        document.addEventListener('click', setElementContainsTarget);
        return () => {
            document.removeEventListener('click', setElementContainsTarget);
        };
    }, [elementRef, isElementVisible]);
    return isClickedOutside;
};
const useClickOutside = (elementRef, handler) => {
    useEffect(() => {
        const listener = (event) => {
            if (!elementRef.current || elementRef.current.contains(event.target)) {
                return;
            }
            handler(event);
        };
        if (elementRef && elementRef.current) {
            document.addEventListener('mousedown', listener);
            document.addEventListener('touchstart', listener);
        }
        return () => {
            if (elementRef && elementRef.current) {
                document.removeEventListener('mousedown', listener);
                document.removeEventListener('touchstart', listener);
            }
        };
    }, [elementRef, handler]);
};

/**
 * Validate children
 * @param params_0 children
 * @param params_1 parent component
 * @param params_2 allowed child component
 * @ignore - internal component.
 * @internal
 */
const useCheckChildrenType = (...params) => {
    useEffect(() => {
        const [children, parentComponent, ...validComponents] = params;
        if (['development', 'test'].includes(process.env.NODE_ENV || '') &&
            !process.env.STORYBOOK) {
            Children.forEach(children, (child) => {
                if (child) {
                    const childElement = child;
                    const isValid = validComponents.reduce((previousValue, component) => previousValue || childElement.type === component, false);
                    if (!isValid) {
                        throw new Error(`'${parentComponent.displayName}' children should be of type ${validComponents
                            .map((c) => `'${c.displayName}'`)
                            .join(', ')}`);
                    }
                }
            });
        }
    }, params);
};

function withForwardRef(Component) {
    const displayName = Component.displayName || Component.name || 'Component';
    const ComponentWithForwardRef = React.forwardRef((props, ref) => jsx(Component, Object.assign({ forwardRef: ref }, props)));
    ComponentWithForwardRef.displayName = `withForwardRef(${displayName})`;
    return ComponentWithForwardRef;
}

export { setRef, useCheckChildrenType, useClickOutside, useClickedOutside, useForkRef, withForwardRef };
//# sourceMappingURL=index.esm.js.map
